import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import {
    fetchAllProductsMinimalWithUnits,
    fetchProductReportData,
} from "../api/api";
import { AccountingScopeSelect, useAccountingScope } from "./Accounting/accountingscope";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const formatNumber = (value, maximumFractionDigits = 2) => {
    const numericValue = Number(value || 0);
    return numericValue.toLocaleString(undefined, { maximumFractionDigits });
};

const formatPercent = (value) => `${formatNumber(value, 2)}%`;

const getHighestConversionUnit = (productOption) => {
    if (!productOption?.units || productOption.units.length === 0) {
        return null;
    }

    return productOption.units.reduce((highestUnit, currentUnit) => {
        const currentFactor = Number(currentUnit?.conversion_factor || 0);
        const highestFactor = Number(highestUnit?.conversion_factor || 0);
        return currentFactor > highestFactor ? currentUnit : highestUnit;
    }, null);
};

// Function to format the quantity with the highest unit equivalent
const formatQuantityWithHighestUnitEquivalent = (quantitySold, productOption) => {
    const quantityLabel = formatNumber(quantitySold);
    const highestUnit = getHighestConversionUnit(productOption);

    if (!highestUnit) {
        return quantityLabel;
    }

    const highestFactor = Number(highestUnit.conversion_factor || 0);
    if (!Number.isFinite(highestFactor) || highestFactor <= 1) {
        return quantityLabel;
    }

    const equivalentHighestUnits = Number(quantitySold || 0) / highestFactor;
    return `${quantityLabel} (${formatNumber(equivalentHighestUnits)})`;
};
// end of function

const rankingColors = {
    profit: "bg-primary text-white",
    quantitySold: "bg-success text-white",
    revenue: "bg-warning",
    slowMoving: "bg-secondary text-white",
};

const chartPalette = ["#0d6efd", "#198754", "#dc3545", "#fd7e14", "#6f42c1"];

const buildQuantityComparisonChart = (comparison) => ({
    labels: comparison.labels || [],
    datasets: (comparison.series || []).map((series, index) => ({
        label: series.product_name,
        data: series.quantity_sold || [],
        borderColor: chartPalette[index % chartPalette.length],
        backgroundColor: "transparent",
        tension: 0.25,
    })),
});

const buildSingleMetricChart = (labels, label, data, borderColor) => ({
    labels: labels || [],
    datasets: [
        {
            labels,
            data: data || [],
            borderColor,
            backgroundColor: "transparent",
            tension: 0.25,
        },
    ],
});

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: "top",
        },
    },
};

const renderRankingList = (rows, renderValue, emptyText) => {
    if (!rows || rows.length === 0) {
        return <div className="small">{emptyText}</div>;
    }

    return (
        <ol className="mb-0 ps-3">
            {rows.map((row, index) => (
                <li key={`${row.product_id}-${index}`} className="mb-2">
                    <div className="fw-semibold">{row.product_name}</div>
                    <div className="small">{renderValue(row)}</div>
                </li>
            ))}
        </ol>
    );
};

const SummaryCard = ({ title, value, subtitle, className = "bg-light" }) => (
    <Col md={3} className="mb-3">
        <Card className={className}>
            <Card.Body>
                <Card.Title>{title}</Card.Title>
                <Card.Text className="fs-4 fw-bold mb-1">{value}</Card.Text>
                {subtitle ? <div className="small">{subtitle}</div> : null}
            </Card.Body>
        </Card>
    </Col>
);

function ProductsReport() {
    const navigate = useNavigate();
    const scope = useAccountingScope();
    const today = new Date().toISOString().split("T")[0];

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [productOptions, setProductOptions] = useState([]);
    const [selectedProductOption, setSelectedProductOption] = useState(null);
    const [compareProductOptions, setCompareProductOptions] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const loadStaticContext = async () => {
            try {
                const products = await fetchAllProductsMinimalWithUnits();
                setProductOptions(
                    (products || []).map((product) => ({
                        value: product.id,
                        label: product.product_name,
                        units: product.units || [],
                        base_unit: product.base_unit || null,
                    }))
                );
            } catch (loadError) {
                console.error("Error loading product report context:", loadError);
                setError("Unable to load product report filters right now.");
            }
        };

        loadStaticContext();
    }, []);

    const loadReport = async ({
        nextStartDate = startDate,
        nextEndDate = endDate,
        nextStoreId = scope.storeId,
        nextSelectedProduct = selectedProductOption,
        nextCompareOptions = compareProductOptions,
    } = {}) => {
        setLoading(true);
        setError("");

        try {
            const data = await fetchProductReportData({
                start_date: nextStartDate,
                end_date: nextEndDate,
                store_id: nextStoreId || undefined,
                product_id: nextSelectedProduct?.value,
                compare_product_ids: nextCompareOptions.map((option) => option.value),
            });
            setReportData(data);
        } catch (reportError) {
            console.error("Error loading product report:", reportError);
            setError("Unable to load the product report with the selected filters.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!scope.loadingScope && scope.storeId) {
            loadReport().then(() => null);
        }
    }, [scope.loadingScope, scope.storeId]);

    const availableComparisonOptions = useMemo(() => {
        const selectedProductId = selectedProductOption?.value;
        return productOptions.filter((option) => option.value !== selectedProductId);
    }, [productOptions, selectedProductOption]);

    const handleApplyFilters = async (event) => {
        event.preventDefault();
        await loadReport();
    };

    const handleSelectedProductChange = async (option) => {
        setSelectedProductOption(option);
        setCompareProductOptions((prevOptions) =>
            prevOptions.filter((compareOption) => compareOption.value !== option?.value).slice(0, 5)
        );
        await loadReport({
            nextSelectedProduct: option,
            nextCompareOptions: compareProductOptions.filter((compareOption) => compareOption.value !== option?.value).slice(0, 5),
        });
    };

    const handleCompareProductsChange = async (options) => {
        const limitedOptions = (options || []).slice(0, 5);
        setCompareProductOptions(limitedOptions);
        await loadReport({ nextCompareOptions: limitedOptions });
    };

    const selectedProduct = reportData?.selected_product;
    const selectedTrend = selectedProduct?.trend || {};
    const comparisonChart = buildQuantityComparisonChart(reportData?.comparison || {});

    return (
        <>
            <div className="back-button-container">
                <Button variant="danger" onClick={() => navigate(-1)}>Back</Button>
            </div>

            <Container className="mt-4">
                <h1 className="mb-3">Product Report</h1>
                {/*<p className="text-muted">*/}
                {/*    Warehouse users default to all stores and can narrow down to a specific branch. Other stores stay scoped*/}
                {/*    to their active store automatically.*/}
                {/*</p>*/}

                <Form onSubmit={handleApplyFilters} className="mb-4">
                    <Row className="align-items-end">
                        <Col md={3} className="mb-3">
                            <Form.Group>
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} className="mb-3">
                            <Form.Group>
                                <Form.Label>End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        {scope.isAdmin && (
                            <Col md={3} className="mb-3">
                                <AccountingScopeSelect scope={scope} label="Product Report Scope" />
                            </Col>
                        )}
                        <Col md={3} className="mb-3">
                            <Button type="submit" className="w-100">
                                Apply Filters
                            </Button>
                        </Col>
                    </Row>
                </Form>

                {reportData?.scope && (
                    <Alert variant="light" className="border">
                        Showing <strong>{reportData.scope.start_date}</strong> to <strong>{reportData.scope.end_date}</strong> for{" "}
                        <strong>{reportData.scope.effective_store_name}</strong>.
                    </Alert>
                )}

                {error ? <Alert variant="danger">{error}</Alert> : null}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        <Row className="mb-4">
                            <SummaryCard
                                title="Number of Products"
                                value={formatNumber(reportData?.totals?.product_count, 0)}
                                subtitle={`Products with stock in ${reportData?.scope?.effective_store_name || "selected scope"}`}
                                className="bg-primary text-white"
                            />
                            <SummaryCard
                                title="Stock Value"
                                value={formatNumber(reportData?.totals?.stock_value)}
                                subtitle={`Inventory value in ${reportData?.scope?.effective_store_name || "selected scope"}`}
                                className="bg-success text-white"
                            />
                        </Row>

                        <Row className="mb-4">
                            <Col lg={3} className="mb-3">
                                <Card className={rankingColors.profit}>
                                    <Card.Body>
                                        <Card.Title>Top Profit</Card.Title>
                                        {renderRankingList(
                                            reportData?.rankings?.top_profit,
                                            (row) => `Profit ${formatNumber(row.profit)} | Margin ${formatPercent(row.profit_margin)}`,
                                            "No profitable products for this range."
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} className="mb-3">
                                <Card className={rankingColors.quantitySold}>
                                    <Card.Body>
                                        <Card.Title>Top Quantity Sold</Card.Title>
                                        {renderRankingList(
                                            reportData?.rankings?.top_quantity_sold,
                                            (row) => `${formatNumber(row.quantity_sold)} base units sold`,
                                            "No sales quantities for this range."
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} className="mb-3">
                                <Card className={rankingColors.revenue}>
                                    <Card.Body>
                                        <Card.Title>Top Revenue</Card.Title>
                                        {renderRankingList(
                                            reportData?.rankings?.top_revenue,
                                            (row) => `Revenue ${formatNumber(row.revenue)}`,
                                            "No revenue data for this range."
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} className="mb-3">
                                <Card className={rankingColors.slowMoving}>
                                    <Card.Body>
                                        <Card.Title>Slow Moving Products</Card.Title>
                                        {renderRankingList(
                                            reportData?.rankings?.slow_moving,
                                            (row) => `Stock ${formatNumber(row.current_stock)} | Last move ${row.last_movement_date || "Never"}`,
                                            "No slow movers with stock on hand."
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Card className="mb-4">
                            <Card.Body>
                                <Row>
                                    <Col md={6} className="mb-3">
                                        <Form.Label>Select Product</Form.Label>
                                        <Select
                                            options={productOptions}
                                            value={selectedProductOption}
                                            onChange={handleSelectedProductChange}
                                            placeholder="Search and select a product"
                                            isClearable
                                        />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Form.Label>Compare With Other Products (up to 5)</Form.Label>
                                        <Select
                                            options={availableComparisonOptions}
                                            value={compareProductOptions}
                                            onChange={handleCompareProductsChange}
                                            placeholder="Select comparison products"
                                            isMulti
                                            closeMenuOnSelect={false}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {selectedProduct ? (
                            <>
                                <h3 className="mb-3">{selectedProduct.product_name}</h3>

                                <Row>
                                    <SummaryCard
                                        title="Quantity Sold"
                                        value={formatQuantityWithHighestUnitEquivalent(
                                            selectedProduct.summary.quantity_sold,
                                            selectedProductOption
                                        )}
                                        subtitle={
                                            getHighestConversionUnit(selectedProductOption)
                                            ? `Base units sold in selected period. Bracket shows ${getHighestConversionUnit(selectedProductOption)?.unit?.name}.`
                                            : "Base units sold in selected period."
                                        }
                                        className="bg-info text-white"
                                    />
                                    <SummaryCard
                                        title="Revenue"
                                        value={formatNumber(selectedProduct.summary.revenue)}
                                        subtitle="Net revenue from this product"
                                        className="bg-success text-white"
                                    />
                                    <SummaryCard
                                        title="Profit"
                                        value={formatNumber(selectedProduct.summary.profit)}
                                        subtitle={`Contribution ${formatPercent(selectedProduct.summary.profit_contribution)}`}
                                        className="bg-warning"
                                    />
                                    <SummaryCard
                                        title="Profit Margin"
                                        value={formatPercent(selectedProduct.summary.profit_margin)}
                                        subtitle={`Current stock ${formatNumber(selectedProduct.summary.current_stock)}`}
                                        className="bg-primary text-white"
                                    />
                                </Row>

                                <Row>
                                    <SummaryCard
                                        title="Purchase Quantity"
                                        value={formatNumber(selectedProduct.summary.purchase_quantity)}
                                        subtitle="Stock received from purchases"
                                    />
                                    <SummaryCard
                                        title="Orders"
                                        value={formatNumber(selectedProduct.summary.order_count, 0)}
                                        subtitle="Orders containing this product"
                                    />
                                    <SummaryCard
                                        title="Transfer In"
                                        value={formatNumber(selectedProduct.summary.transfer_in_quantity)}
                                        subtitle="Stock received through transfers"
                                    />
                                    <SummaryCard
                                        title="Transfer Out"
                                        value={formatNumber(selectedProduct.summary.transfer_out_quantity)}
                                        subtitle="Stock moved out through transfers"
                                    />
                                    <SummaryCard
                                        title="Adjustments"
                                        value={formatNumber(selectedProduct.summary.adjustment_net)}
                                        subtitle={`In ${formatNumber(selectedProduct.summary.adjustment_in)} | Out ${formatNumber(selectedProduct.summary.adjustment_out)}`}
                                    />
                                </Row>

                                <Row className="mt-2">
                                    <Col lg={6} className="mb-4">
                                        <Card>
                                            <Card.Body>
                                                <Card.Title>Revenue Trend</Card.Title>
                                                <div style={{ height: "320px" }}>
                                                    <Line
                                                        data={buildSingleMetricChart(
                                                            selectedTrend.labels,
                                                            "Revenue",
                                                            selectedTrend.revenue,
                                                            '#198754'
                                                        )}
                                                        options={chartOptions}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={6} className="mb-4">
                                        <Card>
                                            <Card.Body>
                                                <Card.Title>Quantity Sold Trend</Card.Title>
                                                <div style={{ height: "320px" }}>
                                                    <Line
                                                        data={buildSingleMetricChart(
                                                            selectedTrend.labels,
                                                            "Quantity Sold",
                                                            selectedTrend.quantity_sold,
                                                            '#0d6efd'
                                                        )}
                                                        options={chartOptions}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={6} className="mb-4">
                                        <Card>
                                            <Card.Body>
                                                <Card.Title>Profit Trend</Card.Title>
                                                <div style={{ height: "320px" }}>
                                                    <Line
                                                        data={buildSingleMetricChart(
                                                            selectedTrend.labels,
                                                            "Profit",
                                                            selectedTrend.profit,
                                                            "#dc3545"
                                                        )}
                                                        options={chartOptions}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={6} className="mb-4">
                                        <Card>
                                            <Card.Body>
                                                <Card.Title>Orders Containing Product</Card.Title>
                                                <div style={{ height: "320px" }}>
                                                    <Line
                                                        data={buildSingleMetricChart(
                                                            selectedTrend.labels,
                                                            "Order Count",
                                                            selectedTrend.order_count,
                                                            "#fd7e14"
                                                        )}
                                                        options={chartOptions}
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col lg={12} className="mb-4">
                                        <Card>
                                            <Card.Body>
                                                <Card.Title>Product Comparison by Quantity Sold</Card.Title>
                                                <div style={{ height: "420px" }}>
                                                    <Line data={comparisonChart} options={chartOptions} />
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <Alert variant="info">
                                Select a product to see its detailed revenue, profit, stock-movement cards, and trend charts.
                            </Alert>
                        )}
                    </>
                )}
            </Container>
        </>
    );
}

export default ProductsReport;